import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Flex, Spinner, Center, Button, Icon } from '@chakra-ui/react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useShowToast } from '@/hooks';
import { KDrawer } from '@/ui-library/components';
import { KText } from '@/ui-library/components/text';
import { KSelect } from '@/ui-library/components/molecules/KSelect';
import { Certn, Zinc, SpringVerify } from '@/assets/icons/ats';
import { CheckCircle } from '@kula-ai/icons/dual-tone/CheckCircle';
import { getErrorMessage } from '@/utils/errors';
import {
  getCertnPackages,
  getZincPackages,
  getSpringVerifyPackages,
  sendCertnBackgroundCheck,
  sendZincBackgroundCheck,
  sendSpringVerifyBackgroundCheck,
} from './backgroundCheckApi';
import type {
  ProviderConfigMap,
  AddToBackgroundCheckProps,
  BackgroundCheckPackage,
} from './backgroundCheckTypes';

const PROVIDER_CONFIG: ProviderConfigMap = {
  certn: {
    title: 'Certn',
    icon: Certn,
    queryKey: 'certn-packages',
    getPackages: getCertnPackages,
    sendBackgroundCheck: sendCertnBackgroundCheck,
    successMessage: 'Certn background check sent successfully',
  },
  zinc: {
    title: 'Zinc',
    icon: Zinc,
    queryKey: 'zinc-packages',
    getPackages: getZincPackages,
    sendBackgroundCheck: sendZincBackgroundCheck,
    successMessage: 'Zinc background check sent successfully',
  },
  springverify: {
    title: 'SpringVerify',
    icon: SpringVerify,
    queryKey: 'springverify-packages',
    getPackages: getSpringVerifyPackages,
    sendBackgroundCheck: sendSpringVerifyBackgroundCheck,
    successMessage: 'SpringVerify background check sent successfully',
  },
};

export const AddToBackgroundCheck = ({
  provider,
  modalActions,
  contactDetails,
  refetchApplication,
}: AddToBackgroundCheckProps) => {
  const [selectedPackage, setSelectedPackage] = useState<string>('');
  const toast = useShowToast();
  const config = PROVIDER_CONFIG[provider];
  const isSpringVerify = provider === 'springverify';

  // SpringVerify packages use { package_id, subtype_id, package_name } instead
  // of the certn/zinc { id, name } shape. We key options on the package_id +
  // subtype_id pair so the (required) subtype can be recovered on submit.
  const getPackageValue = useCallback(
    (pkg: BackgroundCheckPackage): string =>
      isSpringVerify ? `${pkg.package_id}_${pkg.subtype_id}` : pkg.id,
    [isSpringVerify]
  );
  const getPackageLabel = (pkg: BackgroundCheckPackage): string =>
    isSpringVerify
      ? pkg.subtype_friendly_name || pkg.package_name || ''
      : pkg.name;

  const {
    data: packagesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [config.queryKey],
    queryFn: config.getPackages,
    staleTime: 0,
    enabled: modalActions.isOpen,
  });

  useEffect(() => {
    if (isError && error) {
      const errorData = (error as any)?.response?.data || error;
      toast({
        title: getErrorMessage(errorData, 'backgroundCheck'),
        status: 'error',
      });
    }
  }, [isError, error, toast]);

  useEffect(() => {
    if (
      packagesData &&
      !isLoading &&
      ((packagesData as any)?.errors?.length > 0 ||
        (packagesData as any)?.error)
    ) {
      toast({
        title: getErrorMessage(packagesData, 'backgroundCheck'),
        status: 'error',
      });
    }
  }, [packagesData, isLoading, toast]);

  const allPackages: BackgroundCheckPackage[] = useMemo(
    () => (packagesData as any)?.packages || [],
    [packagesData]
  );

  // Auto-select when there's exactly one package — mirrors the assessment
  // flows (e.g. HackerEarth) so the user doesn't have to open a single-option
  // dropdown just to pick the only choice. Derived (not synced via state) so
  // the default holds until the user explicitly picks something else.
  const effectiveSelectedPackage =
    selectedPackage ||
    (allPackages.length === 1 ? getPackageValue(allPackages[0]) : '');

  const sendBackgroundCheckMutation = useMutation<any, any, any>({
    mutationFn: config.sendBackgroundCheck,
    onSuccess: (response: any) => {
      const hasError =
        response?.success === false || response?.error || response?.errors;

      if (!hasError) {
        toast({
          title: config.successMessage,
          status: 'success',
        });
        modalActions?.onClose();
        refetchApplication();
        setSelectedPackage('');
      } else {
        toast({
          title: getErrorMessage(response, 'backgroundCheck'),
          status: 'error',
        });
      }
    },
    onError: (error: any) => {
      const errorData = error?.response?.data || error;
      toast({
        title: getErrorMessage(errorData, 'backgroundCheck'),
        status: 'error',
      });
    },
  });

  const handleSendBackgroundCheck = () => {
    const selectedPackageData = allPackages?.find(
      (pkg: BackgroundCheckPackage) =>
        getPackageValue(pkg) === effectiveSelectedPackage
    );

    // SpringVerify expects integer ids plus a required subtype_id, and does not
    // take job id / email.
    if (isSpringVerify) {
      sendBackgroundCheckMutation.mutate({
        ats_application_id: Number(contactDetails?.id),
        package_id: Number(selectedPackageData?.package_id),
        subtype_id: Number(selectedPackageData?.subtype_id),
        package_name: selectedPackageData?.package_name || '',
      });
      return;
    }

    sendBackgroundCheckMutation.mutate({
      ats_application_id: contactDetails?.id?.toString(),
      ats_application_job_id: contactDetails?.job_id?.toString(),
      package_id: effectiveSelectedPackage,
      package_name: selectedPackageData?.name || '',
      email_address: contactDetails?.contact?.email,
    });
  };

  const renderHeader = () => {
    return (
      <>
        <Flex alignItems="center" gap="4px" px="xxl" pt="xxl" pb="12px">
          <KText size="md-medium" color="secondary-900" mr="6px">
            Create
          </KText>
          <Icon as={config.icon} w="24px" h="24px" />
          <KText size="md-medium" color="secondary-900">
            {config.title} background check
          </KText>
        </Flex>
        <Box border="1px solid" borderColor="border-color" />
      </>
    );
  };

  const renderFooter = () => {
    if (isLoading) return null;
    return (
      <Flex
        p="lg"
        borderTop="1px solid"
        borderColor="border-color"
        bg="ats-secondary"
      >
        <Flex ml="auto" gap="12px">
          <Button
            variant="secondary"
            onClick={modalActions.onClose}
            isDisabled={sendBackgroundCheckMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendBackgroundCheck}
            isLoading={sendBackgroundCheckMutation.isPending}
            isDisabled={
              !effectiveSelectedPackage || sendBackgroundCheckMutation.isPending
            }
          >
            Submit
          </Button>
        </Flex>
      </Flex>
    );
  };

  const packageOptions =
    allPackages?.map((pkg: BackgroundCheckPackage) => ({
      label: getPackageLabel(pkg),
      value: getPackageValue(pkg),
    })) || [];

  const selectedOption = packageOptions.find(
    (option) => option.value === effectiveSelectedPackage
  );

  const selectedPackageData = allPackages?.find(
    (pkg: BackgroundCheckPackage) =>
      getPackageValue(pkg) === effectiveSelectedPackage
  );

  // SpringVerify returns account credit info on the packages payload.
  const creditBalance = (packagesData as any)?.credit_balance;
  const costPerCandidate = selectedPackageData?.cost_per_candidate;

  // Helper function to format check type names
  const formatCheckType = (checkType: string): string => {
    return checkType
      .replace(/_/g, ' ')
      .replace(/\d+/g, '')
      .trim()
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <KDrawer
      isOpen={modalActions.isOpen}
      onClose={() => {
        modalActions.onClose();
        setSelectedPackage('');
      }}
      header={renderHeader()}
      footer={renderFooter()}
      showCloseButton
      contentProps={{
        borderTopLeftRadius: 'xl',
        size: 'md',
        maxW: '40%',
      }}
    >
      <Flex h="100%" flexDir="column" px="xxl" py="lg">
        {isLoading ? (
          <Center h="80vh">
            <Spinner size="lg" color="primary-500" />
          </Center>
        ) : (
          <Box>
            <Flex alignItems="center" gap="12px" mb="md">
              <KText as="span" size="sm-semi-bold" color="secondary-900">
                Select package{' '}
                <KText as="span" color="error-200" display="inline-block">
                  *
                </KText>
              </KText>
            </Flex>
            <KSelect
              placeholder="Select a package"
              value={selectedOption}
              onChange={(selectedOption: any) =>
                setSelectedPackage(selectedOption?.value || '')
              }
              options={packageOptions}
              isRequired
              isDisabled={sendBackgroundCheckMutation.isPending}
            />
            {isSpringVerify &&
              (creditBalance !== undefined ||
                costPerCandidate !== undefined) && (
                <Flex mt="md" gap="16px" alignItems="center">
                  {costPerCandidate !== undefined && selectedPackageData && (
                    <KText size="sm-medium" color="secondary-700">
                      Cost per candidate:{' '}
                      <KText
                        as="span"
                        size="sm-semi-bold"
                        color="secondary-900"
                      >
                        {costPerCandidate}{' '}
                        {costPerCandidate === 1 ? 'credit' : 'credits'}
                      </KText>
                    </KText>
                  )}
                  {creditBalance !== undefined && (
                    <KText size="sm-medium" color="secondary-700">
                      Credits available:{' '}
                      <KText
                        as="span"
                        size="sm-semi-bold"
                        color="secondary-900"
                      >
                        {creditBalance}
                      </KText>
                    </KText>
                  )}
                </Flex>
              )}
            {selectedPackageData && selectedPackageData.checks?.length > 0 && (
              <Box mt="lg">
                <Flex alignItems="center" gap="12px" mb="md">
                  <Flex
                    alignItems="center"
                    gap="8px"
                    bg="secondary-50"
                    px="12px"
                    py="6px"
                    borderRadius="8px"
                  >
                    <KText as="span" size="sm-semi-bold" color="secondary-900">
                      Checks included in the package
                    </KText>
                    <Flex
                      alignItems="center"
                      bg="base-white"
                      px="8px"
                      py="2px"
                      borderRadius="6px"
                      border="1px solid"
                      borderColor="border-color"
                    >
                      <KText
                        as="span"
                        size="sm-semi-bold"
                        color="secondary-700"
                      >
                        {selectedPackageData.checks.length}
                      </KText>
                    </Flex>
                  </Flex>
                  <Box
                    borderBottom="1px solid"
                    borderColor="border-color"
                    flex={1}
                  />
                </Flex>
                <Flex flexDir="column" gap="md">
                  {selectedPackageData.checks.map((check, index) => (
                    <Flex key={index} alignItems="center" gap="12px">
                      <Icon
                        as={CheckCircle}
                        w="24px"
                        h="24px"
                        color="success-200"
                        flexShrink={0}
                      />
                      <KText size="md-regular" color="secondary-900">
                        {formatCheckType(check.check_type)}
                      </KText>
                    </Flex>
                  ))}
                </Flex>
              </Box>
            )}
          </Box>
        )}
      </Flex>
    </KDrawer>
  );
};

export const AddToCertn = (
  props: Omit<AddToBackgroundCheckProps, 'provider'>
) => <AddToBackgroundCheck {...props} provider="certn" />;

export const AddToZinc = (
  props: Omit<AddToBackgroundCheckProps, 'provider'>
) => <AddToBackgroundCheck {...props} provider="zinc" />;

export const AddToSpringVerify = (
  props: Omit<AddToBackgroundCheckProps, 'provider'>
) => <AddToBackgroundCheck {...props} provider="springverify" />;
